<script setup lang="ts">
    import { useSurgicalGuideOrders } from "@/stores/surgicalGuideOrders/store";
    import { computed, ref } from "vue";
    import { logger } from "@/util/logger";
    import { useSurgicalGuideCase } from "@/stores/surgicalGuide/caseStore";
    import { SURGICAL_APPROACHES } from "@/domain/case/surgery";
    import { SurgicalGuideOrderState } from "@/stores/surgicalGuideOrders/types";

    const props = defineProps<Props>();

    const log = logger('SurgicalGuideDownloads');

    export interface Props {
        caseId: number
    }

    const ordersStore = useSurgicalGuideOrders()
    const caseSurgicalGuide = useSurgicalGuideCase();

    const isDownloadingScan = ref(false);
    const isDownloadingModels = ref(false);

    // Kept here rather than thrown: an error out of a click handler reaches
    // Vue.config.errorHandler, which raises the app-wide boundary in App.vue.
    const downloadError = ref<string | null>(null);

    const surgicalApproach = computed(() => {
        const option = SURGICAL_APPROACHES.find(
            option => option.value === caseSurgicalGuide.current.surgicalApproachId
        );
        return option?.label || 'Unknown';
    })

    const latestOrder = computed(() => {
        return ordersStore.getLatestOrder()
    })

    const scanDownloaded = computed(() => {
        return ordersStore.getLatestOrder()?.scansLastDownloadedAt != null
    })

    const modelsDownloaded = computed(() => {
        return ordersStore.getLatestOrder()?.modelsLastDownloadedAt != null
    })

    const downloadScan = async () => {
        if (!latestOrder.value) {
            log.warning('no order found')
            return
        }

        downloadError.value = null;
        isDownloadingScan.value = true;
        try {
            await ordersStore.downloadScan(latestOrder.value.caseId, latestOrder.value.id);
        } catch (error) {
            log.error('Failed to download the scan', error);
            downloadError.value = 'Could not download the scan. Please try again.';
        } finally {
            await ordersStore.load(props.caseId);
            isDownloadingScan.value = false;
        }
    }

    const downloadModels = async () => {
        if (!latestOrder.value) {
            log.warning('no order found')
            return
        }

        downloadError.value = null;
        isDownloadingModels.value = true;
        try {
            await ordersStore.downloadModels(latestOrder.value.caseId, latestOrder.value.id);
        } catch (error) {
            log.error('Failed to download the models', error);
            downloadError.value = 'Could not download the models. Please try again.';
        } finally {
            await ordersStore.load(props.caseId);
            isDownloadingModels.value = false;
        }
    }

</script>

<template>
    <app-main-card
        v-if="latestOrder?.state == SurgicalGuideOrderState.Ordered"
        class="mb-0"
        css-class="pa-6"
    >
        <v-card-title>Downloads</v-card-title>
        <app-text>
            <p>CR Number: {{ caseSurgicalGuide.current.crNumber }}</p>
            <p>Surgical Approach: {{ surgicalApproach }} </p>
            <p>Plan ID: {{latestOrder.planId}}</p>
        </app-text>

        <app-divider>
        </app-divider>
        <!-- These will trigger to true even if the download is cancelled-->
        <div class="status-container">
            <v-alert
                class="rounded-pill text-end"
                :class="scanDownloaded ? 'ordered' : 'ready'"
                :border="false"
            >
                {{ scanDownloaded ? 'Scans Downloaded' : 'Scan Ready to Download' }}
            </v-alert>
            <v-alert
                class="rounded-pill text-end"
                :class="modelsDownloaded ? 'ordered' : 'ready'"
                :border="false"
            >
                {{ modelsDownloaded ? 'Models Downloaded' : 'Models Ready to Download' }}
            </v-alert>
        </div>

        <app-divider>
        </app-divider>

        <div class="downloads-container">
            <app-button-secondary :loading="isDownloadingScan" @click="downloadScan">
                <v-icon>mdi-download</v-icon>
                Download Scan
            </app-button-secondary>
            <app-button-primary :loading="isDownloadingModels" @click="downloadModels">
                <v-icon>mdi-download</v-icon>
                Download Models
            </app-button-primary>
        </div>

        <v-alert
            v-if="downloadError"
            class="mt-4"
            type="error"
            variant="tonal"
            density="compact"
        >
            {{ downloadError }}
        </v-alert>
    </app-main-card>
</template>

<style scoped lang="scss">

    .status-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px
    }

    .downloads-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px
    }

    .ready {
        background-color: rgb(var(--v-theme-primary), 0.18);
        color: rgb(var(--v-theme-main-blue));
        padding: 3px 10px;
        width: fit-content;
        font-size: 12px;
    }

    :deep(.v-card-title) {
        padding: 0 0 20px;
    }
</style>
